import React, { useCallback } from "react";
import { Container, Row } from "../components";
import { Spacer } from "./spacer";
import { useMediaQuery } from "../hooks/use_media_query";
import styles from "./block_upsell_layout.module.css";
import { UpsellCard } from "./template_catalog_base";
import { useAnalytics } from "@/lib/analytics/analytics";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { Button } from "react-bootstrap";
import {
  CatalogUpsellTextAlign,
  CatalogUpsellVersionedImage,
} from "@/data/catalog";
import { ConditionalLink } from "./conditional_link";
interface UpsellBlockLayoutProps {
  title: string;
  description: string;
  image: string;
  versionedImage?: CatalogUpsellVersionedImage;
  learnMoreURL: string;
  layout:
    | "left_to_right"
    | "right_to_left"
    | "default"
    | "hero"
    | "hero_overlay";
  ctaLabel?: string;
  longDescription?: string;
  textAlign: CatalogUpsellTextAlign;
}

const UpsellBlockLayout = (props: UpsellBlockLayoutProps) => {
  const {
    image,
    versionedImage,
    title,
    description,
    learnMoreURL,
    layout,
    ctaLabel,
    longDescription,
    textAlign,
  } = props;
  const { fbt } = useI18n();
  const rtl = layout === "right_to_left";
  const mq = useMediaQuery();
  const dataTestId = "catalog-upsell-card";

  const analytics = useAnalytics();

  const onUpsellClick = useCallback(
    (e) => {
      analytics.event(
        "Upsell Learn More Clicked",
        {
          Upsell: e.target.href,
          Source: "WL",
          Layout: layout,
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
    },
    [analytics, layout]
  );

  if (layout === "default" || layout === "hero" || layout === "hero_overlay") {
    return (
      <UpsellCard
        title={title}
        image={image}
        versionedImage={versionedImage}
        learnMoreURL={learnMoreURL}
        description={description}
        longDescription={longDescription}
        ctaLabel={ctaLabel}
        layout={layout}
        textAlign={textAlign}
        onClickButton={onUpsellClick}
      />
    );
  }

  return (
    <div>
      {mq.device === "desktop" ? (
        <Row justifyContent="space-between" rtl={rtl} testID={dataTestId}>
          <div
            style={{
              padding: 32,
              backgroundColor: "rgba(var(--bs-primary-rgb), 0.15)",
              minWidth: 300,
              maxWidth: 400,
              minHeight: 320,
              borderRadius: rtl ? "0 4px 4px 0" : "4px 0 0 4px",
            }}
            className="catalog-upsell-card-desc-container d-flex flex-column justify-content-center"
          >
            <h3 className={styles.title}>{title}</h3>
            <Spacer size={8} />
            {longDescription ? (
              <div
                className="description"
                dangerouslySetInnerHTML={{ __html: longDescription }}
              />
            ) : (
              <div className="description">{description}</div>
            )}
            {learnMoreURL && (
              <>
                <Spacer size={24} />
                <ConditionalLink
                  linkClassName="upsell-learn-more-button"
                  href={learnMoreURL}
                  onClick={onUpsellClick}
                >
                  <Button variant="primary">
                    {ctaLabel || fbt("Learn more")}
                  </Button>
                </ConditionalLink>
              </>
            )}
          </div>

          <Container flex={1}>
            <ConditionalLink href={learnMoreURL} onClick={onUpsellClick}>
              <img
                alt={title}
                className="position-absolute top-0 start-0 w-100 h-100 object-fit-cover"
                style={{
                  borderRadius: rtl ? "4px 0 0 4px" : "0 4px 4px 0",
                }}
                src={versionedImage?.large.url || image}
              />
            </ConditionalLink>
          </Container>
        </Row>
      ) : (
        <Container testID={dataTestId}>
          <ConditionalLink href={learnMoreURL} onClick={onUpsellClick}>
            <img
              alt={title}
              style={{
                objectFit: "cover",
                width: "100%",
                height: 240,
                borderTopLeftRadius: 5,
                borderTopRightRadius: 5,
              }}
              src={image}
            />
          </ConditionalLink>
          <Container
            backgroundColor="rgba(var(--bs-primary-rgb), 0.15)"
            className="catalog-upsell-card-desc-container"
            padding={24}
            flex={1}
          >
            <h3 className={styles.title}>{title}</h3>
            {longDescription ? (
              <div
                className="description"
                dangerouslySetInnerHTML={{ __html: longDescription }}
              />
            ) : (
              <div className="description">{description}</div>
            )}
            <Spacer size={16} />
            {learnMoreURL && (
              <div>
                <ConditionalLink
                  linkClassName="upsell-learn-more-button"
                  href={learnMoreURL}
                  onClick={onUpsellClick}
                >
                  <Button style={{ width: "auto" }} variant="primary">
                    {ctaLabel || fbt("Learn more")}
                  </Button>
                </ConditionalLink>
              </div>
            )}
          </Container>
        </Container>
      )}
    </div>
  );
};

export { UpsellBlockLayout };
